import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { toast } from 'react-toastify';
import {
  UserIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  PhoneIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import { Button, Input } from '../components/ui';
import { authService } from '../services/auth.service';
import { useAuthStore } from '../stores';

const acceptInviteSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  phoneNumber: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type AcceptInviteFormData = z.infer<typeof acceptInviteSchema>;

export const AcceptInvite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<{ email: string; companyName: string; roleName?: string } | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcceptInviteFormData>({
    resolver: zodResolver(acceptInviteSchema),
  });

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValid(false);
        setError('Invalid or missing invitation link');
        setIsValidating(false);
        return;
      }

      try {
        const result = await authService.validateInvite(token);
        setIsValid(result.valid);
        if (result.valid && result.invitation) {
          setInvitation(result.invitation);
        } else {
          setError('This invitation is invalid or has expired');
        }
      } catch (err: any) {
        setIsValid(false);
        setError(err.response?.data?.error || 'This invitation is invalid or has expired');
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const onSubmit = async (data: AcceptInviteFormData) => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await authService.acceptInvite({
        token,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
        phoneNumber: data.phoneNumber,
      });

      // Set auth state
      useAuthStore.setState({
        token: result.token,
        user: result.user,
        business: result.business,
        permissions: result.permissions,
        isAuthenticated: true,
      });

      toast.success(`Welcome to ${invitation?.companyName}!`);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create your account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while validating token
  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-secondary-900">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-secondary-600 dark:text-secondary-400">Validating your invitation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-white dark:bg-secondary-900">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 py-12 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {/* Logo */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                <span className="text-white font-bold text-lg">CH</span>
              </div>
              <span className="font-display text-xl font-semibold text-secondary-900 dark:text-secondary-50">
                Capture Health
              </span>
            </div>

            {!isValid ? (
              <>
                <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-danger-100 dark:bg-danger-900/30">
                  <ExclamationTriangleIcon className="w-8 h-8 text-danger-600 dark:text-danger-400" />
                </div>
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50 text-center">Invalid invitation</h2>
                <p className="mt-2 text-secondary-600 dark:text-secondary-400 text-center">
                  {error}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">You're invited!</h2>
                <p className="mt-2 text-secondary-600 dark:text-secondary-400">
                  Complete your account to join <strong>{invitation?.companyName}</strong>
                </p>
                {invitation?.roleName && (
                  <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                    You'll be joining as: <strong>{invitation.roleName}</strong>
                  </p>
                )}
              </>
            )}
          </div>

          {!isValid ? (
            <div className="space-y-4">
              <Link to="/login">
                <Button className="w-full" size="lg">
                  Go to sign in
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Email (readonly) */}
              <div>
                <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-1">
                  Email address
                </label>
                <div className="px-4 py-3 bg-secondary-50 dark:bg-secondary-800 rounded-lg text-secondary-600 dark:text-secondary-400">
                  {invitation?.email}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="First name"
                  placeholder="John"
                  leftIcon={<UserIcon className="h-5 w-5" />}
                  error={errors.firstName?.message}
                  {...register('firstName')}
                />
                <Input
                  label="Last name"
                  placeholder="Doe"
                  error={errors.lastName?.message}
                  {...register('lastName')}
                />
              </div>

              <Input
                label="Phone number (optional)"
                type="tel"
                placeholder="(555) 123-4567"
                leftIcon={<PhoneIcon className="h-5 w-5" />}
                error={errors.phoneNumber?.message}
                {...register('phoneNumber')}
              />

              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Create a password"
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                }
                error={errors.password?.message}
                {...register('password')}
              />

              <Input
                label="Confirm password"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Confirm your password"
                leftIcon={<LockClosedIcon className="h-5 w-5" />}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                }
                error={errors.confirmPassword?.message}
                {...register('confirmPassword')}
              />

              {error && (
                <div className="p-3 rounded-lg bg-danger-50 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoading}
              >
                Create account
              </Button>

              <p className="text-xs text-center text-secondary-500 dark:text-secondary-400">
                By creating an account, you agree to our Terms of Service and Privacy Policy.
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:block relative flex-1 gradient-mesh bg-secondary-50 dark:bg-secondary-800">
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-lg text-center">
            <div className="w-24 h-24 mx-auto mb-8 rounded-2xl bg-white dark:bg-secondary-700 shadow-elevated flex items-center justify-center">
              <BuildingOfficeIcon className="w-12 h-12 text-primary-500" />
            </div>
            <h3 className="text-2xl font-display font-semibold text-secondary-900 dark:text-secondary-50 mb-4">
              {invitation?.companyName || 'Join Your Team'}
            </h3>
            <p className="text-secondary-600 dark:text-secondary-400">
              Get started with Capture Health to manage telehealth operations, orders, and patients.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
